import React, { useState, useEffect } from "react";
import "./FlowSidebar.css";

const TOUCHPOINT_MAP = {
  instagram_follow: { label: "Follow", icon: "fa-instagram", isBrand: true },
  instagram_like: { label: "Like Post", icon: "fa-instagram", isBrand: true },
  instagram_comment: { label: "Comment", icon: "fa-instagram", isBrand: true },
  instagram_dm: { label: "DM", icon: "fa-instagram", isBrand: true },
  instagram_voice: { label: "Voice DM", icon: "fa-instagram", isBrand: true },
  instagram_story_reply: { label: "Story Reply", icon: "fa-instagram", isBrand: true },
  instagram_story_mention: { label: "Story Mention", icon: "fa-instagram", isBrand: true },
  instagram_reel_comment: { label: "Reel Comment", icon: "fa-instagram", isBrand: true },
  linkedin_connect: { label: "Connect", icon: "fa-linkedin", isBrand: true },
  linkedin_connected: { label: "Connected", icon: "fa-linkedin", isBrand: true },
  linkedin_message: { label: "Message", icon: "fa-linkedin", isBrand: true },
  linkedin_like: { label: "Like Post", icon: "fa-linkedin", isBrand: true },
  linkedin_comment: { label: "Comment", icon: "fa-linkedin", isBrand: true },
  linkedin_follow: { label: "Follow", icon: "fa-linkedin", isBrand: true },
  linkedin_voice: { label: "Voice Msg", icon: "fa-linkedin", isBrand: true },
  whatsapp_message: { label: "Message", icon: "fa-whatsapp", isBrand: true },
  whatsapp_voice: { label: "Voice Msg", icon: "fa-whatsapp", isBrand: true },
  whatsapp_call: { label: "Call", icon: "fa-whatsapp", isBrand: true },
  email_sent: { label: "Email", icon: "fa-paper-plane", isBrand: false },
  cold_email: { label: "Cold Email", icon: "fa-snowflake", isBrand: false },
  follow_up_email: { label: "Follow Up", icon: "fa-reply", isBrand: false },
  phone_call: { label: "Phone Call", icon: "fa-phone-alt", isBrand: false },
};

const getStepInfo = (step) => {
  if (!step) return { label: "?", icon: "fa-question", isBrand: false };
  if (step.nodeType === "delay") {
    const d = parseInt(step.delayDays) || 0;
    return { label: `${d}d Wait`, icon: "fa-hourglass-half", isBrand: false };
  }
  if (step.nodeType === "tag") return { label: "Tags", icon: "fa-tags", isBrand: false };
  if (step.nodeType === "end") {
    const t = step.endType || "completed";
    const dis = ["disqualified", "not_interested", "wrong_contact"].includes(t);
    return { label: dis ? "Disqualified" : "Completed", icon: dis ? "fa-ban" : "fa-flag-checkered", isBrand: false, isEnd: true, isDis: dis };
  }
  if (step.nodeType === "condition") return { label: step.customQuestion || "Decision", icon: "fa-question", isBrand: false };
  if (step.nodeType === "split") return { label: "A/B Split", icon: "fa-code-branch", isBrand: false };
  if (step.nodeType === "auto_send_email") return { label: "Auto Email", icon: "fa-paper-plane", isBrand: false, isAuto: true };
  if (step.nodeType === "auto_check_reply") return { label: "Check Reply", icon: "fa-envelope-open-text", isBrand: false, isAuto: true };
  if (step.nodeType === "data_capture") return { label: "Capture Data", icon: "fa-database", isBrand: false };
  if (step.nodeType === "log_notes") return { label: "Log Notes", icon: "fa-clipboard-list", isBrand: false };
  return TOUCHPOINT_MAP[step.type] || { label: step.type || "Action", icon: "fa-tasks", isBrand: false };
};

const BRANCH_TYPES = new Set(["condition", "split", "auto_check_reply"]);


/**
 * Build adjacency map from edges: { [sourceId]: { default|yes|no|a|b: targetId } }
 */
const buildAdjMap = (edges) => {
  const adj = {};
  for (const e of edges) {
    if (!adj[e.source]) adj[e.source] = {};
    const handle = e.sourceHandle || "default";
    adj[e.source][handle] = e.target;
  }
  return adj;
};

/**
 * Walk the DAG from a start node and return a tree structure for rendering.
 * Returns an array of "segments" — each segment is either a linear node or a branch block.
 */
const buildFlowTree = (flow) => {
  if (!flow?.steps?.length) return [];
  const edges = flow.visualData?.edges || [];
  const adj = buildAdjMap(edges);
  const stepsById = {};
  flow.steps.forEach((s, i) => { stepsById[s.id] = { step: s, stepIndex: i }; });

  const visited = new Set();

  const walk = (nodeId) => {
    const segments = [];
    let currentId = nodeId;

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const entry = stepsById[currentId];
      if (!entry) break;
      const { step, stepIndex } = entry;

      if (BRANCH_TYPES.has(step.nodeType)) {
        const isSplit = step.nodeType === "split";
        const isReply = step.nodeType === "auto_check_reply";
        const yesHandle = isSplit ? "a" : "yes";
        const noHandle = isSplit ? "b" : "no";
        const yesLabel = isSplit ? "A" : isReply ? "Reply" : "Yes";
        const noLabel = isSplit ? "B" : isReply ? "No Reply" : "No";
        const yesTarget = adj[currentId]?.[yesHandle];
        const noTarget = adj[currentId]?.[noHandle];

        segments.push({
          type: "branch",
          step,
          stepIndex,
          yesLabel,
          noLabel,
          yesBranch: yesTarget ? walk(yesTarget) : [],
          noBranch: noTarget ? walk(noTarget) : [],
        });
        break;
      }

      segments.push({ type: "node", step, stepIndex });

      const nextId = adj[currentId]?.default;
      currentId = nextId;
    }
    return segments;
  };

  return walk(flow.steps[0]?.id);
};

const FlowSidebar = ({
  warmupFlows,
  contacts,
  currentContact,
  selectedStepFilter,
  onStepFilterChange,
  onRemovedClick,
  isOpen,
  onClose,
}) => {
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [manualFlowSelection, setManualFlowSelection] = useState(false);
  const [branchTabs, setBranchTabs] = useState({});

  useEffect(() => {
    if (manualFlowSelection) return;
    const flowId = currentContact?.warmupFlow?.flowId;
    if (flowId && flowId !== selectedFlowId) {
      setSelectedFlowId(flowId);
    }
  }, [currentContact?.warmupFlow?.flowId, manualFlowSelection, selectedFlowId]);

  useEffect(() => {
    if (!selectedFlowId && warmupFlows.length > 0) {
      setSelectedFlowId(warmupFlows[0].id);
    }
  }, [warmupFlows, selectedFlowId]);

  const selectedFlow = warmupFlows.find((f) => f.id === selectedFlowId);

  const contactsByStep = {};
  let totalInFlow = 0;
  contacts.forEach((c) => {
    if (c.warmupFlow?.flowId === selectedFlowId) {
      const idx = c.warmupFlow.currentStepIndex;
      if (!contactsByStep[idx]) contactsByStep[idx] = [];
      contactsByStep[idx].push(c);
      totalInFlow++;
    }
  });

  const handleFlowChange = (flowId) => {
    setManualFlowSelection(true);
    setSelectedFlowId(flowId);
    setBranchTabs({});
    onStepFilterChange(null);
  };

  const handleStepClick = (stepIndex) => {
    if (selectedStepFilter?.stepIndex === stepIndex && selectedStepFilter?.flowId === selectedFlowId) {
      onStepFilterChange(null);
    } else {
      onStepFilterChange({ flowId: selectedFlowId, stepIndex });
    }
  };

  const toggleBranchTab = (nodeId, tab) => {
    setBranchTabs((prev) => ({
      ...prev,
      [nodeId]: prev[nodeId] === tab ? null : tab,
    }));
  };

  const tree = selectedFlow ? buildFlowTree(selectedFlow) : [];

  const currentContactStepIdx =
    currentContact?.warmupFlow?.flowId === selectedFlowId
      ? currentContact.warmupFlow.currentStepIndex
      : null;

  const renderNode = (step, stepIndex) => {
    const info = getStepInfo(step);
    const count = contactsByStep[stepIndex]?.length || 0;
    const isActive = selectedStepFilter?.stepIndex === stepIndex && selectedStepFilter?.flowId === selectedFlowId;
    const isCurrent = currentContactStepIdx === stepIndex;

    if (step.nodeType === "tag") return null;

    return (
      <button
        key={step.id}
        className={`fs-node ${isActive ? "fs-node--active" : ""} ${isCurrent ? "fs-node--current" : ""} ${info.isEnd ? (info.isDis ? "fs-node--dis" : "fs-node--end") : ""} ${info.isAuto ? "fs-node--auto" : ""}`}
        onClick={() => handleStepClick(stepIndex)}
        title={info.label}
      >
        <div className="fs-node__icon">
          <i className={`${info.isBrand ? "fab" : "fa"} ${info.icon}`}></i>
        </div>
        <span className="fs-node__label">{info.label}</span>
        {count > 0 && <span className="fs-node__count">{count}</span>}
      </button>
    );
  };

  const renderSegments = (segments) => {
    return segments.map((seg, idx) => {
      if (seg.type === "node") {
        const node = renderNode(seg.step, seg.stepIndex);
        if (!node) return null;
        return (
          <div key={seg.step.id} className="fs-seg">
            {idx > 0 && <div className="fs-line" />}
            {node}
          </div>
        );
      }

      if (seg.type === "branch") {
        const info = getStepInfo(seg.step);
        const count = contactsByStep[seg.stepIndex]?.length || 0;
        const isActive = selectedStepFilter?.stepIndex === seg.stepIndex && selectedStepFilter?.flowId === selectedFlowId;
        const isCurrent = currentContactStepIdx === seg.stepIndex;
        const activeTab = branchTabs[seg.step.id] || null;

        return (
          <div key={seg.step.id} className="fs-seg">
            {idx > 0 && <div className="fs-line" />}
            <button
              className={`fs-node fs-node--condition ${isActive ? "fs-node--active" : ""} ${isCurrent ? "fs-node--current" : ""}`}
              onClick={() => handleStepClick(seg.stepIndex)}
              title={info.label}
            >
              <div className="fs-node__icon">
                <i className={`fa ${info.icon}`}></i>
              </div>
              <span className="fs-node__label">{info.label}</span>
              {count > 0 && <span className="fs-node__count">{count}</span>}
            </button>

            {/* Branch tabs — expand to show sub-steps with counts */}
            {(() => {
              const yesTotal = seg.yesBranch.reduce((sum, s) => sum + (contactsByStep[s.stepIndex]?.length || 0), 0);
              const noTotal = seg.noBranch.reduce((sum, s) => sum + (contactsByStep[s.stepIndex]?.length || 0), 0);

              return (
                <>
                  <div className="fs-branch-tabs">
                    <button
                      className={`fs-branch-tab fs-branch-tab--yes ${activeTab === "yes" ? "fs-branch-tab--active" : ""}`}
                      onClick={() => toggleBranchTab(seg.step.id, "yes")}
                    >
                      {seg.yesLabel}
                      {yesTotal > 0 && <span className="fs-branch-tab__count">{yesTotal}</span>}
                      <i className={`fa fa-chevron-${activeTab === "yes" ? "down" : "right"} fs-branch-tab__arrow`}></i>
                    </button>
                    <button
                      className={`fs-branch-tab fs-branch-tab--no ${activeTab === "no" ? "fs-branch-tab--active" : ""}`}
                      onClick={() => toggleBranchTab(seg.step.id, "no")}
                    >
                      {seg.noLabel}
                      {noTotal > 0 && <span className="fs-branch-tab__count">{noTotal}</span>}
                      <i className={`fa fa-chevron-${activeTab === "no" ? "down" : "right"} fs-branch-tab__arrow`}></i>
                    </button>
                  </div>

                  {activeTab === "yes" && seg.yesBranch.length > 0 && (
                    <div className="fs-branch-path">
                      {renderSegments(seg.yesBranch)}
                    </div>
                  )}
                  {activeTab === "no" && seg.noBranch.length > 0 && (
                    <div className="fs-branch-path">
                      {renderSegments(seg.noBranch)}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        );
      }
      return null;
    });
  };

  if (!warmupFlows.length) return null;

  return (
    <>
      {isOpen && <div className="fs-overlay" onClick={onClose} />}
      <aside className={`fs ${isOpen ? "fs--open" : ""}`}>
        <div className="fs__header">
          <span className="fs__title">Flow Map</span>
          <button className="fs__close" onClick={onClose}><i className="fa fa-times"></i></button>
        </div>

        {warmupFlows.length > 1 ? (
          <div className="fs__selector">
            <select
              value={selectedFlowId || ""}
              onChange={(e) => handleFlowChange(e.target.value)}
            >
              {warmupFlows.map((f) => (
                <option key={f.id} value={f.id}>{f.name || "Unnamed Flow"}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="fs__flow-name">{selectedFlow?.name || "Flow"}</div>
        )}

        <div className="fs__summary">
          <span className="fs__summary-count">{totalInFlow}</span>
          <span className="fs__summary-label">active</span>
          {selectedStepFilter && (
            <button className="fs__clear-filter" onClick={() => onStepFilterChange(null)}>
              <i className="fa fa-times"></i> Clear
            </button>
          )}
        </div>

        <div className="fs__tree">
          {selectedFlow && tree.length > 0 ? (
            renderSegments(tree)
          ) : (
            <div className="fs__empty">
              <i className="fa fa-sitemap"></i>
              <span>No steps</span>
            </div>
          )}
        </div>

        {/* Removed – simple button */}
        <div className="fs__removed">
          <button
            className="fs__removed-btn"
            onClick={() => { onRemovedClick(selectedFlowId); onClose(); }}
          >
            <i className="fa fa-user-slash"></i>
            <span>Removed anzeigen</span>
            <i className="fa fa-arrow-right"></i>
          </button>
        </div>
      </aside>
    </>
  );
};

export default FlowSidebar;
